import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Trash2, 
  Ban, 
  CheckCircle, 
  Crown, 
  X,
  Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserData } from '@/services/admin/user-management-service';

interface BulkActionsToolbarProps {
  selectedUsers: UserData[];
  onClearSelection: () => void;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onBulkDelete: () => void;
  onBulkExport: () => void;
  onBulkSubscriptionUpdate: (plan: string) => void;
}

export const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedUsers,
  onClearSelection,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  onBulkExport,
  onBulkSubscriptionUpdate,
}) => {
  if (selectedUsers.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-muted/50 border rounded-lg gap-3 sm:gap-0">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="px-2 py-1 text-xs">
            {selectedUsers.length} selected
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClearSelection}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clear selection</TooltipContent>
          </Tooltip>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
          {/* Status Actions */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkActivate}
                className="h-10 w-10 p-0 sm:h-8 sm:w-8"
              >
                <CheckCircle className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Activate users</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkDeactivate}
                className="h-10 w-10 p-0 sm:h-8 sm:w-8"
              >
                <Ban className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Deactivate users</TooltipContent>
          </Tooltip>

          {/* Subscription Updates */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-10 w-10 p-0 sm:h-8 sm:w-8">
                    <Crown className="h-5 w-5 sm:h-4 sm:w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Update subscription</TooltipContent>
            </Tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onBulkSubscriptionUpdate('free')}>
                Set to Free
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onBulkSubscriptionUpdate('premium')}>
                Set to Premium
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onBulkSubscriptionUpdate('enterprise')}>
                Set to Enterprise
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Export */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkExport}
                className="h-10 w-10 p-0 sm:h-8 sm:w-8"
              >
                <Download className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export selected</TooltipContent>
          </Tooltip>

          {/* Destructive Actions */}
          <div className="w-px h-6 bg-border mx-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                onClick={onBulkDelete}
                className="h-10 w-10 p-0 sm:h-8 sm:w-8"
              >
                <Trash2 className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete users</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};